import type { TFunction } from "i18next";

import {
  DATE_FORMAT_DMY,
  formatDatetime,
  OAUTH_SCOPE_WRITE,
  type IOauthGrantListItemDto,
  type TimeZoneEnum,
} from "@ryde/shared";

import {
  CONNECT_AGENT_PAGE_QUERY,
  type IConnectedAppRow,
} from "./connected-apps.models";

/** Grant timestamps are UTC ISO strings; they are shown in the company's own time zone like every other date. */
export const toConnectedAppRow = ({
  grant,
  t,
  timeFormat,
  timezone,
}: {
  grant: IOauthGrantListItemDto;
  t: TFunction;
  timeFormat: string;
  timezone: TimeZoneEnum;
}): IConnectedAppRow => {
  const format = (dateTimeISO: TDateISO): string =>
    formatDatetime({
      dateTimeISO,
      dateFormat: DATE_FORMAT_DMY,
      timeFormat,
      timezone,
    });

  return {
    grant,
    permissionsLabel: grant.scope.includes(OAUTH_SCOPE_WRITE)
      ? `${t("oauthConsent.scopeRead")}, ${t("oauthConsent.scopeWrite")}`
      : t("oauthConsent.scopeRead"),
    connectedAtLabel: format(grant.createdAt),
    lastUsedAtLabel: grant.lastUsedAt
      ? format(grant.lastUsedAt)
      : t("connectedApps.neverUsed"),
  };
};

/** Without a base only an absolute URL parses; with one a relative path resolves against it. */
const toAbsoluteUrl = ({
  value,
  base,
}: {
  value: string | undefined;
  base?: string;
}): URL | undefined => {
  if (!value?.trim()) {
    return undefined;
  }

  try {
    return new URL(value.trim(), base);
  } catch {
    return undefined;
  }
};

/**
 * Link to the MCP server's public "connect your AI" page, in the dashboard's language and with the
 * company's logo. Undefined when the deployment publishes no page, so the section shows no link.
 *
 * The logo setting may hold a path relative to the dashboard (a few companies store a bare file
 * name), so it is resolved against the dashboard's own address; the page decides whether to show it.
 */
export const buildConnectAgentPageUrl = ({
  documentationUrl,
  language,
  logoPath,
  appOrigin,
}: {
  documentationUrl: string | undefined;
  language: string;
  logoPath: string | undefined;
  appOrigin: string;
}): string | undefined => {
  const pageUrl = toAbsoluteUrl({ value: documentationUrl });

  if (!pageUrl || !["https:", "http:"].includes(pageUrl.protocol)) {
    return undefined;
  }

  pageUrl.searchParams.set(CONNECT_AGENT_PAGE_QUERY.language, language);

  const logoUrl = toAbsoluteUrl({ value: logoPath, base: `${appOrigin}/` });
  if (logoUrl) {
    pageUrl.searchParams.set(CONNECT_AGENT_PAGE_QUERY.logo, logoUrl.href);
  }

  return pageUrl.href;
};
